<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import KanbanBoard from './components/KanbanBoard.vue';
import TaskCard from './components/TaskCard.vue';
import TaskDrawer from './components/TaskDrawer.vue';
import TaskListView from './components/TaskListView.vue';
import tasksAPI from 'dashboard/api/tasks';

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const viewMode = ref('grid'); // 'grid' ou 'list'
const sortBy = ref('created_at');
const sortOrder = ref('desc');
const priorityFilter = ref('all');
const assigneeFilter = ref('all');
const showSortMenu = ref(false);
const showFilterMenu = ref(false);

// Récupérer les agents de l'espace et l'utilisateur actuel
const agents = useMapGetter('agents/getVerifiedAgents');
const currentUser = useMapGetter('getCurrentUser');

// Fonction pour charger les colonnes depuis localStorage (pour l'UI/colonnes custom)
const loadColumnsConfig = () => {
  const saved = localStorage.getItem('chatwoot_kanban_columns_config');
  if (saved) {
    return JSON.parse(saved);
  }
  return null;
};

// Fonction pour sauvegarder la configuration des colonnes dans localStorage
const saveColumnsConfig = columnsStructure => {
  localStorage.setItem(
    'chatwoot_kanban_columns_config',
    JSON.stringify(columnsStructure)
  );
};

// Plus besoin de mapping, chaque colonne a son propre status unique maintenant

// Configuration des colonnes (structure UI, couleurs, ordre)
const columnsConfig = ref(
  loadColumnsConfig() || {
    backlog: {
      id: 'backlog',
      title: 'Backlog',
      color: '[color:#64748b]',
      bgColor: '[background-color:#64748b]',
      order: 0,
    },
    inProgress: {
      id: 'inProgress',
      title: 'En cours',
      color: '[color:#8b5cf6]',
      bgColor: '[background-color:#8b5cf6]',
      order: 1,
    },
    review: {
      id: 'review',
      title: 'En révision',
      color: '[color:#3b82f6]',
      bgColor: '[background-color:#3b82f6]',
      order: 2,
    },
    done: {
      id: 'done',
      title: 'Terminé',
      color: '[color:#22c55e]',
      bgColor: '[background-color:#22c55e]',
      order: 3,
    },
  }
);

// Tâches chargées depuis l'API
const tasks = ref([]);

// Calculer les colonnes avec les tâches
const columns = computed(() => {
  const cols = {};

  Object.entries(columnsConfig.value).forEach(([key, config]) => {
    const columnTasks = tasks.value
      .filter(task => {
        // Le status de la tâche doit correspondre à l'ID de la colonne
        return task.status === key;
      })
      .sort((a, b) => (a.position || 0) - (b.position || 0));

    cols[key] = {
      ...config,
      tasks: columnTasks,
    };
  });

  return cols;
});

// Gestion des colonnes personnalisées
const showColumnModal = ref(false);
const columnDialog = ref(null);
const editingColumn = ref(null);
const columnForm = ref({
  title: '',
  color: '',
  bgColor: '',
  hexColor: '#64748b',
});

// Couleurs prédéfinies suggérées
const colorPresets = [
  '#64748b', // Slate
  '#3b82f6', // Blue
  '#0ea5e9', // Sky
  '#06b6d4', // Cyan
  '#14b8a6', // Teal
  '#10b981', // Emerald
  '#22c55e', // Green
  '#84cc16', // Lime
  '#eab308', // Yellow
  '#f59e0b', // Amber
  '#f97316', // Orange
  '#ef4444', // Red
  '#f43f5e', // Rose
  '#ec4899', // Pink
  '#d946ef', // Fuchsia
  '#a855f7', // Purple
  '#8b5cf6', // Violet
  '#6366f1', // Indigo
];

const searchQuery = ref('');

// Gestion du drawer éditable
const showTaskDrawer = ref(false);
const selectedTask = ref(null);

// Charger les tâches depuis l'API
const loadTasks = async () => {
  try {
    isLoading.value = true;
    const response = await tasksAPI.get();
    tasks.value = response.data.map(task => ({
      id: String(task.id), // Convertir en String pour cohérence
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      position: task.position || 0,
      dueDate: task.due_date,
      assignees: task.assigned_user ? [task.assigned_user.id] : [],
      createdBy: task.created_by?.id,
      createdAt: task.created_at,
      url: task.url || '',
      files: task.files || [],
      tags: task.tags || [],
    }));
  } catch (error) {
    useAlert(t('TASKS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Colonnes triées par ordre
const sortedColumns = computed(() => {
  return Object.entries(columns.value)
    .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
});

// Filtrer et trier les tâches (pour la vue liste)
const filteredTasks = computed(() => {
  let filtered = [...tasks.value];

  // Filtre par recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(task => {
      const matchesText =
        task.title.toLowerCase().includes(query) ||
        task.description?.toLowerCase().includes(query);

      const matchesAssignees = task.assignees?.some(agentId => {
        const agent = agents.value.find(a => a.id === agentId);
        return agent?.name.toLowerCase().includes(query);
      });

      return matchesText || matchesAssignees;
    });
  }

  // Filtre par priorité
  if (priorityFilter.value !== 'all') {
    filtered = filtered.filter(task => task.priority === priorityFilter.value);
  }

  // Filtre par assigné
  if (assigneeFilter.value !== 'all') {
    if (assigneeFilter.value === 'unassigned') {
      filtered = filtered.filter(
        task => !task.assignees || task.assignees.length === 0
      );
    } else {
      filtered = filtered.filter(task =>
        task.assignees?.includes(parseInt(assigneeFilter.value, 10))
      );
    }
  }

  // Tri
  filtered.sort((a, b) => {
    let comparison = 0;

    switch (sortBy.value) {
      case 'title': {
        comparison = a.title.localeCompare(b.title);
        break;
      }
      case 'priority': {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        comparison =
          (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
        break;
      }
      case 'due_date': {
        const aDate = a.dueDate ? new Date(a.dueDate) : new Date(0);
        const bDate = b.dueDate ? new Date(b.dueDate) : new Date(0);
        comparison = aDate - bDate;
        break;
      }
      case 'created_at':
      default:
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
        break;
    }

    return sortOrder.value === 'asc' ? comparison : -comparison;
  });

  return filtered;
});

// Filtrer et trier les colonnes (pour la vue grid/kanban)
const filteredColumns = computed(() => {
  const columnsToFilter = sortedColumns.value;
  const filtered = {};

  Object.entries(columnsToFilter).forEach(([key, column]) => {
    let columnTasks = [...column.tasks];

    // Filtre par recherche
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase();
      columnTasks = columnTasks.filter(task => {
        const matchesText =
          task.title.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query);

        const matchesAssignees = task.assignees?.some(agentId => {
          const agent = agents.value.find(a => a.id === agentId);
          return agent?.name.toLowerCase().includes(query);
        });

        return matchesText || matchesAssignees;
      });
    }

    // Filtre par priorité
    if (priorityFilter.value !== 'all') {
      columnTasks = columnTasks.filter(
        task => task.priority === priorityFilter.value
      );
    }

    // Filtre par assigné
    if (assigneeFilter.value !== 'all') {
      if (assigneeFilter.value === 'unassigned') {
        columnTasks = columnTasks.filter(
          task => !task.assignees || task.assignees.length === 0
        );
      } else {
        columnTasks = columnTasks.filter(task =>
          task.assignees?.includes(parseInt(assigneeFilter.value, 10))
        );
      }
    }

    // Tri
    columnTasks.sort((a, b) => {
      let comparison = 0;

      switch (sortBy.value) {
        case 'title': {
          comparison = a.title.localeCompare(b.title);
          break;
        }
        case 'priority': {
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          comparison =
            (priorityOrder[a.priority] || 0) - (priorityOrder[b.priority] || 0);
          break;
        }
        case 'due_date': {
          const aDate = a.dueDate ? new Date(a.dueDate) : new Date(0);
          const bDate = b.dueDate ? new Date(b.dueDate) : new Date(0);
          comparison = aDate - bDate;
          break;
        }
        case 'created_at':
        default:
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
      }

      return sortOrder.value === 'asc' ? comparison : -comparison;
    });

    filtered[key] = {
      ...column,
      tasks: columnTasks,
    };
  });

  return filtered;
});

// Gérer le déplacement d'une tâche
const handleTaskMove = async ({ taskId, toColumn, newIndex }) => {
  try {
    // Trouver la tâche (convertir les IDs en nombre pour comparaison)
    const taskIndex = tasks.value.findIndex(
      taskItem => String(taskItem.id) === String(taskId)
    );
    if (taskIndex === -1) {
      return;
    }

    const task = tasks.value[taskIndex];

    // Le status est maintenant l'ID de la colonne
    const newStatus = toColumn;

    // Créer un nouvel objet pour forcer la réactivité
    tasks.value[taskIndex] = {
      ...task,
      status: newStatus,
      position: newIndex,
    };

    // Mettre à jour sur le serveur
    await tasksAPI.update(task.id, {
      status: newStatus,
      position: newIndex,
    });

    useAlert(t('TASKS.TASK_MOVED'));
  } catch (error) {
    useAlert(t('TASKS.UPDATE_ERROR'));
    // Recharger pour annuler le changement local
    await loadTasks();
  }
};

// Ouvrir le drawer pour voir/éditer une tâche
const openTaskDrawer = task => {
  // Le status reste au format backend
  selectedTask.value = {
    ...task,
  };
  showTaskDrawer.value = true;
};

// Ouvrir le drawer pour créer une tâche
const openCreateTaskDrawer = (columnId = 'backlog') => {
  selectedTask.value = {
    id: null,
    title: '',
    description: '',
    priority: 'medium',
    assignees: [],
    dueDate: '',
    status: columnId, // Le status est l'ID de la colonne
    createdBy: currentUser.value?.id || null,
    createdAt: new Date().toISOString(),
    url: '',
    files: [],
    tags: [],
  };
  showTaskDrawer.value = true;
};

// Fermer le drawer
const closeTaskDrawer = () => {
  showTaskDrawer.value = false;
  selectedTask.value = null;
};

// Sauvegarder une tâche depuis le drawer
const saveTaskFromDrawer = async updatedTask => {
  try {
    // Le status est l'ID de la colonne
    const taskStatus = updatedTask.status;

    if (updatedTask.id) {
      // Modification
      const taskData = {
        title: updatedTask.title,
        description: updatedTask.description,
        status: taskStatus,
        priority: updatedTask.priority,
        due_date: updatedTask.dueDate,
        assigned_user_id: updatedTask.assignees?.[0] || null,
        url: updatedTask.url,
        files: updatedTask.files || [],
        tags: updatedTask.tags || [],
      };
      const response = await tasksAPI.update(updatedTask.id, taskData);

      // Mettre à jour localement avec les données de l'API
      const taskIndex = tasks.value.findIndex(
        taskItem => taskItem.id === updatedTask.id
      );
      if (taskIndex !== -1) {
        const updatedTaskData = {
          id: String(response.data.id),
          title: response.data.title,
          description: response.data.description || '',
          status: response.data.status,
          priority: response.data.priority,
          position: response.data.position || 0,
          dueDate: response.data.due_date,
          assignees: response.data.assigned_user
            ? [response.data.assigned_user.id]
            : [],
          createdBy: response.data.created_by?.id,
          createdAt: response.data.created_at,
          url: response.data.url || '',
          files: response.data.files || [],
          tags: response.data.tags || [],
        };
        tasks.value[taskIndex] = updatedTaskData;
      }

      useAlert(t('TASKS.TASK_UPDATED'));
    } else {
      // Création
      const createData = {
        title: updatedTask.title,
        description: updatedTask.description,
        status: taskStatus,
        priority: updatedTask.priority,
        due_date: updatedTask.dueDate,
        assigned_user_id: updatedTask.assignees?.[0] || null,
        url: updatedTask.url,
        files: updatedTask.files || [],
        tags: updatedTask.tags || [],
      };
      const response = await tasksAPI.create(createData);

      // Ajouter localement
      const newTask = {
        id: String(response.data.id), // Convertir en String
        title: response.data.title,
        description: response.data.description || '',
        status: response.data.status,
        priority: response.data.priority,
        position: response.data.position || 0,
        dueDate: response.data.due_date,
        assignees: response.data.assigned_user
          ? [response.data.assigned_user.id]
          : [],
        createdBy: response.data.created_by?.id,
        createdAt: response.data.created_at,
        url: response.data.url || '',
        files: response.data.files || [],
        tags: response.data.tags || [],
      };

      tasks.value.push(newTask);

      useAlert(t('TASKS.TASK_CREATED'));
    }

    closeTaskDrawer();

    // Recharger les tâches pour s'assurer de la synchronisation
    await loadTasks();
  } catch (error) {
    useAlert(t('TASKS.SAVE_ERROR'));
  }
};

// Supprimer une tâche depuis le drawer
const deleteTaskFromDrawer = async task => {
  try {
    await tasksAPI.delete(task.id);

    // Supprimer localement
    const taskIndex = tasks.value.findIndex(
      taskItem => taskItem.id === task.id
    );
    if (taskIndex !== -1) {
      tasks.value.splice(taskIndex, 1);
    }

    useAlert(t('TASKS.TASK_DELETED'));
    closeTaskDrawer();
  } catch (error) {
    useAlert(t('TASKS.DELETE_ERROR'));
  }
};

// Convertir hex en style inline
const updateColorFromHex = () => {
  const hex = columnForm.value.hexColor;
  columnForm.value.color = `[color:${hex}]`;
  columnForm.value.bgColor = `[background-color:${hex}]`;
};

// Générer une couleur aléatoire
const randomColor = () => {
  const randomHex =
    '#' +
    Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, '0');
  columnForm.value.hexColor = randomHex.toUpperCase();
  updateColorFromHex();
};

// Extraire la couleur hex depuis une colonne
const getHexFromColumn = column => {
  // Si c'est une couleur custom (format [color:...])
  if (column.color?.startsWith('[color:')) {
    return column.color.match(/#[0-9A-Fa-f]{6}/)?.[0] || '#64748b';
  }
  // Sinon retourner une couleur par défaut
  return '#64748b';
};

// Ouvrir le modal de colonne
const openColumnModal = (column = null) => {
  if (column) {
    editingColumn.value = column;
    const hexColor = getHexFromColumn(column);
    columnForm.value = {
      title: column.title,
      color: column.color,
      bgColor: column.bgColor,
      hexColor,
    };
  } else {
    editingColumn.value = null;
    columnForm.value = {
      title: '',
      color: '[color:#64748b]',
      bgColor: '[background-color:#64748b]',
      hexColor: '#64748b',
    };
  }
  showColumnModal.value = true;

  // Ouvrir le dialog après le prochain tick pour s'assurer qu'il est monté
  setTimeout(() => {
    columnDialog.value?.open();
  }, 0);
};

// Gestion des menus de tri et filtre
const setSortBy = (value, order = 'desc') => {
  sortBy.value = value;
  sortOrder.value = order;
  showSortMenu.value = false;
};

const setPriorityFilter = value => {
  priorityFilter.value = value;
  showFilterMenu.value = false;
};

const setAssigneeFilter = value => {
  assigneeFilter.value = value;
};

const toggleSortMenu = event => {
  event?.stopPropagation();
  showSortMenu.value = !showSortMenu.value;
  showFilterMenu.value = false;
};

const toggleFilterMenu = event => {
  event?.stopPropagation();
  showFilterMenu.value = !showFilterMenu.value;
  showSortMenu.value = false;
};

const closeAllMenus = () => {
  showSortMenu.value = false;
  showFilterMenu.value = false;
};

const closeColumnModal = () => {
  showColumnModal.value = false;
  editingColumn.value = null;
  columnForm.value = {
    title: '',
    color: '[color:#64748b]',
    bgColor: '[background-color:#64748b]',
    hexColor: '#64748b',
  };
};

const saveColumn = () => {
  if (!columnForm.value.title) {
    useAlert(t('TASKS.VALIDATION.COLUMN_TITLE_REQUIRED'));
    return;
  }

  if (editingColumn.value) {
    // Modification
    const columnId = editingColumn.value.id;
    columnsConfig.value[columnId] = {
      ...columnsConfig.value[columnId],
      title: columnForm.value.title,
      color: columnForm.value.color,
      bgColor: columnForm.value.bgColor,
    };
    useAlert(t('TASKS.COLUMN_UPDATED'));
  } else {
    // Création
    const timestamp = Date.now();
    const newColumnId = `column_${timestamp}`;
    const maxOrder = Math.max(
      ...Object.values(columnsConfig.value).map(c => c.order || 0),
      -1
    );

    columnsConfig.value[newColumnId] = {
      id: newColumnId,
      title: columnForm.value.title,
      color: columnForm.value.color,
      bgColor: columnForm.value.bgColor,
      order: maxOrder + 1,
    };
    useAlert(t('TASKS.COLUMN_CREATED'));
  }

  saveColumnsConfig(columnsConfig.value);
  closeColumnModal();
};

// Déplacer une colonne vers la gauche
const moveColumnLeft = columnId => {
  const columnsArray = Object.values(columnsConfig.value).sort(
    (a, b) => a.order - b.order
  );
  const currentIndex = columnsArray.findIndex(c => c.id === columnId);

  if (currentIndex > 0) {
    const temp = columnsArray[currentIndex].order;
    columnsArray[currentIndex].order = columnsArray[currentIndex - 1].order;
    columnsArray[currentIndex - 1].order = temp;

    columnsArray.forEach(col => {
      columnsConfig.value[col.id].order = col.order;
    });

    saveColumnsConfig(columnsConfig.value);
  }
};

// Déplacer une colonne vers la droite
const moveColumnRight = columnId => {
  const columnsArray = Object.values(columnsConfig.value).sort(
    (a, b) => a.order - b.order
  );
  const currentIndex = columnsArray.findIndex(c => c.id === columnId);

  if (currentIndex < columnsArray.length - 1) {
    const temp = columnsArray[currentIndex].order;
    columnsArray[currentIndex].order = columnsArray[currentIndex + 1].order;
    columnsArray[currentIndex + 1].order = temp;

    columnsArray.forEach(col => {
      columnsConfig.value[col.id].order = col.order;
    });

    saveColumnsConfig(columnsConfig.value);
  }
};

// Supprimer une colonne
const deleteColumn = columnId => {
  const columnTasks = tasks.value.filter(task => task.status === columnId);

  if (columnTasks.length > 0) {
    useAlert(t('TASKS.COLUMN_DELETE_ERROR_HAS_TASKS'));
    return;
  }

  delete columnsConfig.value[columnId];
  saveColumnsConfig(columnsConfig.value);
  useAlert(t('TASKS.COLUMN_DELETED'));
  closeColumnModal();
};

onMounted(async () => {
  // Charger les agents de l'espace
  store.dispatch('agents/get');

  // Charger les tâches depuis l'API
  await loadTasks();

  document.addEventListener('click', closeAllMenus);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeAllMenus);
});
</script>

<template>
  <section
    class="flex flex-col w-full h-full overflow-hidden bg-[#F9F9F9] dark:bg-[#0F0F10]"
  >
    <!-- Header -->
    <header class="sticky top-0 z-10">
      <div
        class="flex items-start sm:items-center justify-between w-full py-6 px-6 gap-2 mx-auto max-w-[60rem]"
      >
        <span
          class="text-xl font-medium truncate text-[#1D1D1F] dark:text-white"
        >
          {{ t('TASKS.TITLE') }}
        </span>

        <div class="flex items-center flex-col sm:flex-row flex-shrink-0 gap-4">
          <!-- Recherche -->
          <div class="flex items-center gap-2 w-full">
            <Input
              :model-value="searchQuery"
              type="search"
              :placeholder="t('TASKS.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-8 [&:not(.focus)]:!border-transparent bg-white dark:bg-[#1D1D1F] ltr:!pl-8 !py-1 rtl:!pr-8 border border-[#E5E7EB] dark:border-[#2A2A2C]',
              ]"
              class="w-full"
              @input="searchQuery = $event.target.value"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-[#6B7280] dark:text-[#9CA3AF] size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
          </div>

          <div class="flex items-center flex-shrink-0 gap-4">
            <!-- Sort & Filter -->
            <div class="flex items-center gap-2">
              <!-- Sort button -->
              <div class="relative">
                <Button
                  icon="i-lucide-arrow-up-down"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{
                    'bg-[#3860F9]/10 text-[#3860F9] dark:bg-[#D3FD58]/10 dark:text-[#D3FD58]':
                      showSortMenu,
                  }"
                  @click="toggleSortMenu"
                />
                <!-- Sort menu -->
                <div
                  v-if="showSortMenu"
                  class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-white/95 dark:bg-[#1D1D1F]/95 backdrop-blur-[100px] border border-[#E5E7EB] dark:border-[#2A2A2C] min-w-[200px] z-50"
                  @click.stop
                >
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        sortBy === 'created_at',
                    }"
                    @click="setSortBy('created_at', 'desc')"
                  >
                    <span class="i-lucide-clock size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.SORT.DATE')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        sortBy === 'title',
                    }"
                    @click="setSortBy('title', 'asc')"
                  >
                    <span class="i-lucide-text size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.SORT.TITLE')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        sortBy === 'priority',
                    }"
                    @click="setSortBy('priority', 'desc')"
                  >
                    <span
                      class="i-lucide-alert-circle size-3.5 flex-shrink-0"
                    />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.SORT.PRIORITY')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        sortBy === 'due_date',
                    }"
                    @click="setSortBy('due_date', 'asc')"
                  >
                    <span class="i-lucide-calendar size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.SORT.DUE_DATE')
                    }}</span>
                  </button>
                </div>
              </div>

              <!-- Filter button -->
              <div class="relative">
                <Button
                  icon="i-lucide-filter"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{
                    'bg-[#3860F9]/10 text-[#3860F9] dark:bg-[#D3FD58]/10 dark:text-[#D3FD58]':
                      showFilterMenu ||
                      priorityFilter !== 'all' ||
                      assigneeFilter !== 'all',
                  }"
                  @click="toggleFilterMenu"
                />
                <!-- Filter menu -->
                <div
                  v-if="showFilterMenu"
                  class="absolute top-full right-0 mt-1 p-1 rounded-md shadow-xl bg-white/95 dark:bg-[#1D1D1F]/95 backdrop-blur-[100px] border border-[#E5E7EB] dark:border-[#2A2A2C] min-w-[200px] z-50"
                  @click.stop
                >
                  <p
                    class="px-2 py-1 text-[10px] font-semibold text-[#6B7280] dark:text-[#9CA3AF] uppercase tracking-wide"
                  >
                    {{ t('TASKS.FILTER.PRIORITY') }}
                  </p>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        priorityFilter === 'all',
                    }"
                    @click="setPriorityFilter('all')"
                  >
                    <span class="i-lucide-list size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.FILTER.ALL')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        priorityFilter === 'high',
                    }"
                    @click="setPriorityFilter('high')"
                  >
                    <span
                      class="i-lucide-chevrons-up size-3.5 flex-shrink-0 text-red-600"
                    />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.PRIORITY.HIGH')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        priorityFilter === 'medium',
                    }"
                    @click="setPriorityFilter('medium')"
                  >
                    <span
                      class="i-lucide-chevron-up size-3.5 flex-shrink-0 text-woot-500"
                    />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.PRIORITY.MEDIUM')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        priorityFilter === 'low',
                    }"
                    @click="setPriorityFilter('low')"
                  >
                    <span
                      class="i-lucide-chevron-down size-3.5 flex-shrink-0 text-green-600"
                    />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.PRIORITY.LOW')
                    }}</span>
                  </button>

                  <hr
                    class="m-1 rounded border-b border-[#E5E7EB] dark:border-[#2A2A2C]"
                  />

                  <p
                    class="px-2 py-1 text-[10px] font-semibold text-[#6B7280] dark:text-[#9CA3AF] uppercase tracking-wide"
                  >
                    {{ t('TASKS.FILTER.ASSIGNEE') }}
                  </p>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        assigneeFilter === 'all',
                    }"
                    @click="setAssigneeFilter('all')"
                  >
                    <span class="i-lucide-users size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.FILTER.ALL')
                    }}</span>
                  </button>
                  <button
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        assigneeFilter === 'unassigned',
                    }"
                    @click="setAssigneeFilter('unassigned')"
                  >
                    <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      t('TASKS.FILTER.UNASSIGNED')
                    }}</span>
                  </button>
                  <hr
                    class="m-1 rounded border-b border-[#E5E7EB] dark:border-[#2A2A2C]"
                  />
                  <button
                    v-for="agent in agents"
                    :key="agent.id"
                    class="w-full min-h-7 flex items-center flex-nowrap p-1 rounded-md overflow-hidden text-xs text-[#1D1D1F] dark:text-white hover:bg-[#3860F9] dark:hover:bg-[#D3FD58] hover:text-white dark:hover:text-[#1D1D1F] cursor-pointer"
                    :class="{
                      'bg-[#3860F9] text-white dark:bg-[#D3FD58] dark:text-[#1D1D1F]':
                        assigneeFilter === String(agent.id),
                    }"
                    @click="setAssigneeFilter(String(agent.id))"
                  >
                    <span class="i-lucide-user size-3.5 flex-shrink-0" />
                    <span class="mx-2 truncate flex-1 text-left">{{
                      agent.name
                    }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="w-px h-4 bg-[#E5E7EB] dark:bg-[#2A2A2C]" />

            <!-- Toggle vue Grid / Liste -->
            <div class="flex items-center gap-2">
              <div class="relative">
                <Button
                  icon="i-lucide-list"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{
                    'bg-[#3860F9]/10 text-[#3860F9] dark:bg-[#D3FD58]/10 dark:text-[#D3FD58]':
                      viewMode === 'list',
                  }"
                  @click="viewMode = 'list'"
                />
              </div>
              <div class="relative">
                <Button
                  icon="i-lucide-layout-grid"
                  color="slate"
                  size="sm"
                  variant="ghost"
                  :class="{
                    'bg-[#3860F9]/10 text-[#3860F9] dark:bg-[#D3FD58]/10 dark:text-[#D3FD58]':
                      viewMode === 'grid',
                  }"
                  @click="viewMode = 'grid'"
                />
              </div>
            </div>

            <div class="w-px h-4 bg-[#E5E7EB] dark:bg-[#2A2A2C]" />

            <!-- Bouton ajouter une colonne (seulement en mode grid) -->
            <Button
              v-if="viewMode === 'grid'"
              :label="t('TASKS.ADD_COLUMN')"
              icon="i-lucide-plus"
              size="sm"
              variant="outline"
              color="slate"
              @click="openColumnModal()"
            />

            <div
              v-if="viewMode === 'grid'"
              class="w-px h-4 bg-[#E5E7EB] dark:bg-[#2A2A2C]"
            />

            <!-- Bouton ajouter une tâche -->
            <Button
              :label="t('TASKS.ADD_TASK')"
              icon="i-lucide-plus"
              size="sm"
              @click="openCreateTaskDrawer()"
            />
          </div>
        </div>
      </div>
    </header>

    <!-- Kanban Board -->
    <main class="flex-1 overflow-x-auto overflow-y-hidden">
      <div class="w-full h-full">
        <!-- Loading state -->
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-[#6B7280] dark:text-[#9CA3AF]"
        >
          <Spinner />
        </div>

        <!-- Grid view (Kanban) -->
        <div v-else-if="viewMode === 'grid'" class="px-6 py-4 h-full">
          <KanbanBoard
            :columns="filteredColumns"
            @task-move="handleTaskMove"
            @open-create-drawer="openCreateTaskDrawer"
            @column-edit="openColumnModal"
          >
            <template #task="{ task }">
              <TaskCard :task="task" @view="openTaskDrawer(task)" />
            </template>
          </KanbanBoard>
        </div>

        <!-- List view -->
        <div
          v-else-if="viewMode === 'list'"
          class="px-6 py-4 h-full overflow-y-auto"
        >
          <TaskListView
            :tasks="filteredTasks"
            :columns="columnsConfig"
            @view-task="openTaskDrawer"
          />
        </div>
      </div>
    </main>

    <!-- Task Drawer éditable -->
    <TaskDrawer
      :show="showTaskDrawer"
      :task="selectedTask"
      :columns="columnsConfig"
      :agents="agents"
      @close="closeTaskDrawer"
      @save="saveTaskFromDrawer"
      @delete="deleteTaskFromDrawer"
    />

    <!-- Modal de création/édition de colonne -->
    <Dialog
      v-if="showColumnModal"
      ref="columnDialog"
      type="edit"
      width="md"
      :title="
        editingColumn
          ? t('TASKS.EDIT_COLUMN_MODAL.TITLE')
          : t('TASKS.ADD_COLUMN')
      "
      :description="t('TASKS.COLUMN_MODAL.DESCRIPTION')"
      :confirm-button-label="t('TASKS.COLUMN_MODAL.SAVE')"
      @confirm="saveColumn"
      @close="closeColumnModal"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="columnForm.title"
          :label="t('TASKS.COLUMN_MODAL.TITLE_LABEL')"
          :placeholder="t('TASKS.COLUMN_MODAL.TITLE_PLACEHOLDER')"
          required
        />

        <div class="flex flex-col gap-3">
          <label class="text-sm font-medium text-[#1D1D1F] dark:text-white">
            {{ t('TASKS.COLUMN_MODAL.COLOR_LABEL') }}
          </label>

          <!-- Color Picker -->
          <div class="flex items-start gap-4">
            <div class="relative flex-shrink-0">
              <input
                v-model="columnForm.hexColor"
                type="color"
                class="w-20 h-20 rounded-lg cursor-pointer border-2 border-[#E5E7EB] dark:border-[#2A2A2C] hover:border-[#3860F9] dark:hover:border-[#D3FD58] transition-colors"
                @input="updateColorFromHex"
              />
            </div>

            <div class="flex-1 space-y-2">
              <div class="flex items-stretch gap-2">
                <input
                  v-model="columnForm.hexColor"
                  type="text"
                  :placeholder="t('TASKS.COLUMN_MODAL.HEX_PLACEHOLDER')"
                  class="flex-1 px-3 py-2 text-sm rounded-lg border-2 border-[#E5E7EB] dark:border-[#2A2A2C] bg-white dark:bg-[#1D1D1F] text-[#1D1D1F] dark:text-white focus:outline-none focus:border-[#3860F9] dark:focus:border-[#D3FD58] transition-colors font-mono uppercase"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  maxlength="7"
                  @input="updateColorFromHex"
                />
                <button
                  type="button"
                  class="px-3 py-2 rounded-lg bg-[#F9FAFB] dark:bg-[#1D1D1F] text-[#6B7280] dark:text-[#9CA3AF] hover:bg-[#3860F9]/10 dark:hover:bg-[#D3FD58]/10 hover:text-[#3860F9] dark:hover:text-[#D3FD58] transition-colors flex items-center justify-center"
                  :title="t('TASKS.COLUMN_MODAL.RANDOM_COLOR')"
                  @click="randomColor"
                >
                  <span class="i-lucide-shuffle size-4" />
                </button>
              </div>

              <!-- Couleurs suggérées -->
              <div class="flex items-center gap-2 flex-wrap">
                <button
                  v-for="preset in colorPresets"
                  :key="preset"
                  type="button"
                  class="w-8 h-8 rounded-lg border-2 hover:scale-110 transition-transform"
                  :class="
                    columnForm.hexColor === preset
                      ? 'border-[#3860F9] dark:border-[#D3FD58] ring-2 ring-[#3860F9]/30 dark:ring-[#D3FD58]/30'
                      : 'border-[#E5E7EB] dark:border-[#2A2A2C]'
                  "
                  :style="{ backgroundColor: preset }"
                  :title="preset"
                  @click="
                    columnForm.hexColor = preset;
                    updateColorFromHex();
                  "
                />
              </div>
            </div>
          </div>

          <p class="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
            {{ t('TASKS.COLUMN_MODAL.COLOR_HELP') }}
          </p>
        </div>

        <!-- Position (si édition) -->
        <div v-if="editingColumn" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-[#1D1D1F] dark:text-white">
            {{ t('TASKS.COLUMN_MODAL.POSITION_LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <Button
              icon="i-lucide-arrow-left"
              size="sm"
              variant="outline"
              color="slate"
              :label="t('TASKS.COLUMN_MODAL.MOVE_LEFT')"
              @click="moveColumnLeft(editingColumn.id)"
            />
            <Button
              icon="i-lucide-arrow-right"
              size="sm"
              variant="outline"
              color="slate"
              :label="t('TASKS.COLUMN_MODAL.MOVE_RIGHT')"
              @click="moveColumnRight(editingColumn.id)"
            />
          </div>
        </div>

        <!-- Suppression (si édition et pas une colonne par défaut) -->
        <div
          v-if="editingColumn && editingColumn.tasks.length === 0"
          class="pt-4 border-t border-[#E5E7EB] dark:border-[#2A2A2C]"
        >
          <Button
            icon="i-lucide-trash-2"
            size="sm"
            variant="ghost"
            color="slate"
            :label="t('TASKS.COLUMN_MODAL.DELETE')"
            class="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 w-full"
            @click="deleteColumn(editingColumn.id)"
          />
        </div>
        <div
          v-else-if="editingColumn && editingColumn.tasks.length > 0"
          class="pt-4 border-t border-[#E5E7EB] dark:border-[#2A2A2C]"
        >
          <p class="text-xs text-[#6B7280] dark:text-[#9CA3AF]">
            {{ t('TASKS.COLUMN_MODAL.DELETE_INFO') }}
          </p>
        </div>
      </div>
    </Dialog>
  </section>
</template>
